import React, { useState } from 'react';
import { MobileLayout } from '../../components/MobileLayout';
import { Card, CardContent, CardFooter } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { MatchReason } from '../../components/MatchReason';
import { MapPin, Wrench, Layers, Check, Zap, Users, User, Clock, Home } from 'lucide-react';
import { useMaintenance, MaintenanceOpportunity, MaintenanceRequest } from '../../context/MaintenanceContext';
type Filter = 'tutte' | 'aggregabile' | 'vicina' | 'combinazione';
const CATEGORY_LABEL: Record<MaintenanceOpportunity['category'], string> = {
  aggregabile: 'Aggregabile',
  vicina: 'Vicino a un tuo incarico',
  combinazione: 'Combinazione'
};
const FILTERS: {
  id: Filter;
  label: string;
}[] = [{
  id: 'tutte',
  label: 'Tutte'
}, {
  id: 'aggregabile',
  label: 'Aggregabili'
}, {
  id: 'vicina',
  label: 'Vicini a te'
}, {
  id: 'combinazione',
  label: 'Combinazioni'
}];
function SectionHeader({
  title,
  count,
  hint
}: {
  title: string;
  count?: number;
  hint?: string;
}) {
  return <div className="mb-3" data-id="element-3374">
      <div className="flex items-baseline justify-between" data-id="element-3375">
        <h2 className="text-xs text-muted-foreground uppercase tracking-wider font-semibold" data-id="element-3376">
          {title}
        </h2>
        {typeof count === 'number' && <span className="text-xs text-muted-foreground" data-id="element-3377">
            {count} {count === 1 ? 'disponibile' : 'disponibili'}
          </span>}
      </div>
    </div>;
}
// ---- Urgent request card (point 2) ----
function UrgentRequestCard({
  req,
  responded,
  onRespond
}: {
  req: MaintenanceRequest;
  responded: boolean;
  onRespond: () => void;
}) {
  return <Card data-id="element-3378">
      <CardContent data-id="element-3379">
        <div className="flex items-start justify-between gap-3 mb-3" data-id="element-3380">
          <div className="flex items-start gap-3 min-w-0" data-id="element-3381">
            <div className="w-11 h-11 rounded-xl bg-coral/10 flex items-center justify-center flex-shrink-0" data-id="element-3382">
              <Zap size={20} className="text-coral" data-id="element-3383" />
            </div>
            <div className="min-w-0" data-id="element-3384">
              <h3 className="text-sm font-semibold text-foreground" data-id="element-3385">
                {req.title}
              </h3>
              <div className="flex items-center gap-1.5 mt-1" data-id="element-3386">
                <MapPin size={13} className="text-coral flex-shrink-0" data-id="element-3387" />
                <span className="text-sm font-medium text-navy truncate" data-id="element-3388">
                  {req.zone}
                </span>
              </div>
            </div>
          </div>
          <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border bg-coral/5 text-coral border-coral/20 flex-shrink-0" data-id="element-3389">
            <Zap size={11} data-id="element-3390" />
            Urgente
          </span>
        </div>

        <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2 flex-wrap" data-id="element-3391">
          <span className="font-medium text-foreground" data-id="element-3392">
            {req.trade ?? 'Manutenzione'}
          </span>
          <span data-id="element-3393">·</span>
          <span className="inline-flex items-center gap-1" data-id="element-3394">
            <Home size={12} data-id="element-3395" />
            {req.property}
          </span>
          {req.date && req.date !== 'Da definire' && <>
              <span data-id="element-3396">·</span>
              <span className="inline-flex items-center gap-1" data-id="element-3397">
                <Clock size={12} data-id="element-3398" />
                {req.date}
              </span>
            </>}
        </div>

        {req.detail && <p className="text-xs text-muted-foreground mb-2 line-clamp-2" data-id="element-3399">
            {req.detail}
          </p>}

        <p className="text-xs text-muted-foreground flex items-start gap-1.5" data-id="element-3400">
          <MapPin size={13} className="flex-shrink-0 mt-0.5 text-coral" data-id="element-3401" />
          <span data-id="element-3402">
            Richiesta urgente in zona {req.zone}: rispondendo subito hai più
            probabilità di assegnazione.
          </span>
        </p>
      </CardContent>
      <CardFooter data-id="element-3403">
        <span className="text-xs text-muted-foreground" data-id="element-3404">
          Compenso{' '}
          <span className="font-semibold text-foreground" data-id="element-3405">€{req.price}</span>
        </span>
        {responded ? <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600" data-id="element-3406">
            <Check size={15} data-id="element-3407" />
            Candidatura inviata
          </span> : <Button size="sm" onClick={onRespond} data-id="element-3408">
            Rispondi
          </Button>}
      </CardFooter>
    </Card>;
}
// Decorative mini-map thumbnail (mirrors the CM aggregation card visual)
function MiniMap() {
  return <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-xl overflow-hidden flex-shrink-0 relative bg-slate-100 border border-gray-200" data-id="element-3409">
      <svg viewBox="0 0 56 56" className="absolute inset-0 w-full h-full" aria-hidden="true" data-id="element-3410">
        <rect width="56" height="56" fill="#f1f5f9" data-id="element-3411" />
        <path d="M0 20 H56 M0 38 H56 M18 0 V56 M38 0 V56" stroke="#cbd5e1" strokeWidth="2" data-id="element-3412" />
        <rect x="4" y="4" width="10" height="12" fill="#e2e8f0" data-id="element-3413" />
        <rect x="42" y="42" width="10" height="10" fill="#e2e8f0" data-id="element-3414" />
      </svg>
      <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-coral border-2 border-white shadow-sm flex items-center justify-center" data-id="element-3415">
        <MapPin size={9} className="text-white" data-id="element-3416" />
      </span>
    </div>;
}
// Compact calendar-style day chip (mirrors the CM aggregation card date chip)
function DayChip({
  estimateDate
}: {
  estimateDate: string;
}) {
  const parts = estimateDate.trim().split(/\s+/);
  const dayNum = parts[0] ?? '';
  const month = parts[1] ?? '';
  return <div className="flex-shrink-0 w-12 rounded-lg border border-gray-200 bg-white py-1 flex flex-col items-center" data-id="element-3417">
      <span className="text-[9px] uppercase tracking-wider font-medium text-gray-500" data-id="element-3418">
        Stima
      </span>
      <span className="text-base font-bold leading-tight text-navy" data-id="element-3419">
        {dayNum}
      </span>
      <span className="text-[9px] text-muted-foreground" data-id="element-3420">{month}</span>
    </div>;
}
// ---- Aggregation card (point 3) — mirrors the CM aggregation card ----
function AggregationCard({
  agg,
  joined,
  onJoin
}: {
  agg: MaintenanceRequest;
  joined: boolean;
  onJoin: () => void;
}) {
  const filled = agg.aggregation?.filled ?? 0;
  const total = agg.aggregation?.total ?? 0;
  const displayFilled = joined ? Math.min(filled + 1, total) : filled;
  const maxDots = 5;
  const visible = Math.min(displayFilled, maxDots);
  const empty = Math.min(total, maxDots) - visible;
  const [city, area] = agg.zone.split('·').map(s => s.trim());
  return <div className="bg-white border border-border rounded-xl p-4 lg:p-5" data-id="element-3421">
      {/* Header: mini-map + title/zone + estimate chip */}
      <div className="flex items-start justify-between gap-3 mb-4" data-id="element-3422">
        <div className="flex items-start gap-3 min-w-0" data-id="element-3423">
          <MiniMap data-id="element-3424" />
          <div className="min-w-0" data-id="element-3425">
            <h3 className="text-sm font-semibold text-foreground" data-id="element-3426">
              {agg.title}
            </h3>
            <div className="flex items-center gap-1.5 mt-1" data-id="element-3427">
              <MapPin size={14} className="flex-shrink-0 text-coral" data-id="element-3428" />
              <span className="text-sm font-semibold text-navy truncate" data-id="element-3429">
                {area ?? agg.zone}
              </span>
              {city && area && <span className="text-xs text-muted-foreground" data-id="element-3430">· {city}</span>}
            </div>
            <p className="text-xs text-muted-foreground mt-1" data-id="element-3431">
              {agg.trade ?? 'Manutenzione'}
            </p>
          </div>
        </div>
        {agg.aggregation?.estimateDate && <DayChip estimateDate={agg.aggregation.estimateDate} data-id="element-3432" />}
      </div>

      {/* Cluster: one marker per host aderente */}
      <div className="flex items-center gap-3 mb-4" data-id="element-3433">
        <div className="flex items-center -space-x-2" aria-hidden="true" data-id="element-3434">
          {Array.from({
          length: visible
        }).map((_, i) => <span key={`f-${i}`} className="w-7 h-7 rounded-full bg-coral border-2 border-white shadow-sm flex items-center justify-center" data-id="element-3435">
              <User size={12} className="text-white" data-id="element-3436" />
            </span>)}
          {Array.from({
          length: empty
        }).map((_, i) => <span key={`e-${i}`} className="w-7 h-7 rounded-full bg-white border-2 border-dashed border-gray-300 shadow-sm flex items-center justify-center" data-id="element-3437">
              <User size={12} className="text-gray-300" data-id="element-3438" />
            </span>)}
        </div>
        <span className="text-xs text-muted-foreground" data-id="element-3439">
          {displayFilled}/{total} host aderenti
        </span>
      </div>

      {/* Footer: suggested price + CTA */}
      <div className="flex items-center justify-between gap-3 pt-4 border-t border-border" data-id="element-3440">
        <span className="text-xs text-muted-foreground" data-id="element-3441">
          Prezzo suggerito{' '}
          <span className="font-semibold text-foreground" data-id="element-3442">€{agg.price}</span> a
          intervento
        </span>
        {joined ? <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 flex-shrink-0" data-id="element-3443">
            <Check size={15} data-id="element-3444" />
            Candidatura inviata
          </span> : <button onClick={onJoin} className="px-4 h-10 bg-coral text-white rounded-xl text-sm font-semibold hover:bg-coral-600 transition-colors whitespace-nowrap flex-shrink-0" data-id="element-3445">
            Candidati al gruppo
          </button>}
      </div>
    </div>;
}
// Date/time chip for opportunities — renders a calendar chip when the window
// contains a day + month (e.g. "Mar 28 Gen"), otherwise a compact time pill.
function WindowChip({
  window
}: {
  window: string;
}) {
  const [datePart] = window.split(',');
  const tokens = datePart.trim().split(/\s+/);
  const dayIdx = tokens.findIndex(t => /^\d{1,2}$/.test(t));
  const dayNum = dayIdx >= 0 ? tokens[dayIdx] : null;
  const month = dayIdx >= 0 ? tokens[dayIdx + 1] : null;
  const dayName = dayIdx > 0 ? tokens[dayIdx - 1] : null;
  if (dayNum && month) {
    return <div className="flex-shrink-0 w-12 rounded-lg border border-gray-200 bg-white py-1 flex flex-col items-center" data-id="element-3446">
        <span className="text-[9px] uppercase tracking-wider font-medium text-gray-500" data-id="element-3447">
          {dayName ?? 'Quando'}
        </span>
        <span className="text-base font-bold leading-tight text-navy" data-id="element-3448">
          {dayNum}
        </span>
        <span className="text-[9px] text-muted-foreground" data-id="element-3449">{month}</span>
      </div>;
  }
  return <span className="flex-shrink-0 inline-flex items-center gap-1 text-[11px] font-medium text-navy bg-white border border-gray-200 rounded-lg px-2 py-1.5" data-id="element-3450">
      <Clock size={12} className="flex-shrink-0" data-id="element-3451" />
      {window}
    </span>;
}
// ---- Recommended matching opportunity card — mirrors the aggregation card ----
function OpportunityCard({
  o,
  accepted,
  onAccept
}: {
  o: MaintenanceOpportunity;
  accepted: boolean;
  onAccept: () => void;
}) {
  const [city, area] = o.zone.split('·').map(s => s.trim());
  return <div className="bg-white border border-border rounded-xl p-4 lg:p-5" data-id="element-3452">
      {/* Header: mini-map + title/zone/subtitle + date chip */}
      <div className="flex items-start justify-between gap-3 mb-4" data-id="element-3453">
        <div className="flex items-start gap-3 min-w-0" data-id="element-3454">
          <MiniMap data-id="element-3455" />
          <div className="min-w-0" data-id="element-3456">
            <h3 className="text-sm font-semibold text-foreground" data-id="element-3457">{o.title}</h3>
            <div className="flex items-center gap-1.5 mt-1" data-id="element-3458">
              <MapPin size={14} className="flex-shrink-0 text-coral" data-id="element-3459" />
              <span className="text-sm font-semibold text-navy truncate" data-id="element-3460">
                {area ?? o.zone}
              </span>
              {city && area && <span className="text-xs text-muted-foreground" data-id="element-3461">· {city}</span>}
            </div>
            <p className="text-xs text-muted-foreground mt-1" data-id="element-3462">
              {o.trade} · {CATEGORY_LABEL[o.category]}
              {o.jobsCount > 1 ? ` · ${o.jobsCount} interventi` : ''}
            </p>
          </div>
        </div>
        <WindowChip window={o.window} data-id="element-3463" />
      </div>

      {/* Match signals */}
      <div className="mb-4" data-id="element-3464">
        <MatchReason distanceKm={o.distanceKm} efficiency={o.efficiency} peopleInZone={o.hostsInZone} peopleLabel="host in zona" data-id="element-3465" />
      </div>

      {/* Footer: suggested price + CTA */}
      <div className="flex items-center justify-between gap-3 pt-4 border-t border-border" data-id="element-3466">
        <span className="text-xs text-muted-foreground" data-id="element-3467">
          Prezzo suggerito{' '}
          <span className="font-semibold text-foreground" data-id="element-3468">
            €{o.suggestedPrice}
          </span>{' '}
          a intervento
        </span>
        {accepted ? <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 flex-shrink-0" data-id="element-3469">
            <Check size={15} data-id="element-3470" />
            Aggiunto
          </span> : <button onClick={onAccept} className="px-4 h-10 bg-coral text-white rounded-xl text-sm font-semibold hover:bg-coral-600 transition-colors whitespace-nowrap flex-shrink-0" data-id="element-3471">
            Accetta
          </button>}
      </div>
    </div>;
}
export function ManOpportunitaPage() {
  const {
    opportunities,
    openAggregations,
    requests,
    respondToRequest
  } = useMaintenance();
  const [filter, setFilter] = useState<Filter>('tutte');
  const [accepted, setAccepted] = useState<string[]>([]);
  const [responded, setResponded] = useState<string[]>([]);
  const [joined, setJoined] = useState<string[]>([]);
  // Point 2 — urgent requests still looking for a provider
  const urgentRequests = requests.filter(r => r.urgency === 'urgente' && r.status === 'in_ricerca');
  // Point 3 — open aggregations in zone (+ own requests still aggregating)
  const ownAggregating = requests.filter(r => r.status === 'in_attesa_aggregazione');
  const aggregations = [...openAggregations, ...ownAggregating];
  // Matching engine opportunities (recommended)
  const visibleOpps = opportunities.filter(o => filter === 'tutte' ? true : o.category === filter);
  const handleRespond = (id: string) => {
    respondToRequest(id);
    setResponded(p => [...p, id]);
  };
  return <MobileLayout data-id="element-3472">
      <div className="mb-6" data-id="element-3473">
        <h1 className="text-xl lg:text-2xl font-bold text-navy" data-id="element-3474">
          Opportunità di zona
        </h1>
      </div>

      {/* Point 2 — Urgent requests */}
      <section className="mb-8" data-id="element-3475">
        <SectionHeader title="Interventi urgenti" count={urgentRequests.length} hint="Richieste urgenti senza fornitore: rispondi per candidarti." data-id="element-3476" />
        {urgentRequests.length > 0 ? <div className="space-y-3" data-id="element-3477">
            {urgentRequests.map(r => <UrgentRequestCard key={r.id} req={r} responded={responded.includes(r.id)} onRespond={() => handleRespond(r.id)} data-id="element-3478" />)}
          </div> : <div className="bg-white border border-border rounded-xl p-8 text-center" data-id="element-3479">
            <div className="w-11 h-11 rounded-full bg-muted flex items-center justify-center mx-auto mb-3" data-id="element-3480">
              <Zap size={18} className="text-muted-foreground" data-id="element-3481" />
            </div>
            <p className="text-sm font-medium text-foreground" data-id="element-3482">
              Nessun intervento urgente al momento
            </p>
            <p className="text-xs text-muted-foreground mt-1" data-id="element-3483">
              Ti avviseremo non appena arriva una richiesta urgente in zona.
            </p>
          </div>}
      </section>

      {/* Recommended matching opportunities */}
      <section className="mb-8" data-id="element-3484">
        <SectionHeader title="Opportunità consigliate" count={opportunities.length} hint="Interventi vicini e raggruppabili suggeriti dal motore di matching." data-id="element-3485" />

        <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]" data-id="element-3486">
          {FILTERS.map(f => <button key={f.id} onClick={() => setFilter(f.id)} aria-pressed={filter === f.id} className={`flex-shrink-0 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${filter === f.id ? 'bg-coral text-white border-coral' : 'bg-white text-gray-600 border-border hover:border-gray-300'}`} data-id="element-3487">
              {f.label}
            </button>)}
        </div>

        {visibleOpps.length > 0 ? <div className="space-y-3" data-id="element-3488">
            {visibleOpps.map(o => <OpportunityCard key={o.id} o={o} accepted={accepted.includes(o.id)} onAccept={() => setAccepted(p => [...p, o.id])} data-id="element-3489" />)}
          </div> : <div className="bg-white border border-border rounded-xl p-8 text-center" data-id="element-3490">
            <div className="w-11 h-11 rounded-full bg-muted flex items-center justify-center mx-auto mb-3" data-id="element-3491">
              <MapPin size={18} className="text-muted-foreground" data-id="element-3492" />
            </div>
            <p className="text-sm font-medium text-foreground" data-id="element-3493">
              Nessuna opportunità in questa categoria
            </p>
            <p className="text-xs text-muted-foreground mt-1" data-id="element-3494">
              Prova a cambiare filtro per vedere altri interventi in zona.
            </p>
          </div>}
      </section>

      {/* Point 3 — Aggregations in zone */}
      <section data-id="element-3495">
        <SectionHeader title="Raggruppamenti in zona" count={aggregations.length} hint="Gruppi di interventi in formazione: candidati per servirli insieme." data-id="element-3496" />
        {aggregations.length > 0 ? <div className="space-y-3" data-id="element-3497">
            {aggregations.map(agg => <AggregationCard key={agg.id} agg={agg} joined={joined.includes(agg.id)} onJoin={() => setJoined(p => [...p, agg.id])} data-id="element-3498" />)}
          </div> : <div className="bg-white border border-border rounded-xl p-8 text-center" data-id="element-3499">
            <div className="w-11 h-11 rounded-full bg-muted flex items-center justify-center mx-auto mb-3" data-id="element-3500">
              <Layers size={18} className="text-muted-foreground" data-id="element-3501" />
            </div>
            <p className="text-sm font-medium text-foreground" data-id="element-3502">
              Nessun raggruppamento attivo
            </p>
            <p className="text-xs text-muted-foreground mt-1" data-id="element-3503">
              Quando più host richiedono lo stesso intervento in zona, lo vedrai
              qui.
            </p>
          </div>}
      </section>
    </MobileLayout>;
}